
'use client';

import { db, functions } from '@/firebase/config';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/hooks/useAuth';
import { collection, query, orderBy } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Loader2, Check, X, User, Calendar, Mail, AlertCircle, Copy, Hourglass } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { rejectApplication } from '@/lib/actions';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogClose,
} from "@/components/ui/dialog"
import { useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { useCollection } from '@/firebase/firestore/use-collection';
import { useMemoFirebase } from '@/firebase/index';
import { Label } from '../ui/label';
import { Search } from 'lucide-react';

interface Application {
  id: string;
  name: string;
  username: string;
  age: number;
  email: string;
  discordId: string;
  minecraftId: string;
  reason: string;
  source: string;
  country: string;
  province: string;
  minecraftKnowledge: string;
  modKnowledge: string;
  serverActivity: string;
  submissionDate: any; // Can be a Timestamp object
  status: 'pending' | 'approved' | 'rejected' | 'preparing_approval';
}

const knowledgeMapping: Record<string, string> = {
  very_high: "Muy alto",
  high: "Alto",
  normal: "Normal",
  low: "Bajo",
  never_played: "Nunca ha jugado",
  expert: "Experto en mods",
  install: "Sabe instalar mods",
  knowledge_only: "Conoce pero no instala",
  none: "Sin conocimiento de mods",
  daily: "Diario",
  often: "A menudo",
  sometimes: "A veces",
  rarely: "Casi nunca",
  youtube: "YouTube",
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X",
  friend: "Un amigo"
}

const statusConfig = {
    pending: { label: 'PENDIENTE', variant: 'secondary' as const, className: 'text-yellow-500' },
    approved: { label: 'ACEPTADO', variant: 'default' as const, className: 'bg-green-600/80 text-white' },
    rejected: { label: 'RECHAZADO', variant: 'destructive' as const, className: 'bg-red-600/80 text-white' },
    preparing_approval: { label: 'PROCESANDO...', variant: 'default' as const, className: 'bg-blue-600/80 text-white animate-pulse' },
}

export function ApplicationList() {
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const applicationsQuery = useMemoFirebase(() => {
    if (!db) return null;
    return query(collection(db, 'player_applications'), orderBy('submissionDate', 'desc'));
  }, []);

  const { data: applications, isLoading: isLoadingApplications, error, refetch } = useCollection<Application>(applicationsQuery);

  const [isBulkRejecting, setIsBulkRejecting] = useState(false);

  const approveApplication = async (applicationId: string) => {
    setIsProcessing(applicationId);
    try {
        const fn = httpsCallable(functions, 'approvePlayerApplication');
        const res: any = await fn({ applicationId });
        const data = res.data || {};
        const warnings: string[] = data.warnings || [];
        if (warnings.length > 0) {
            toast({
                title: 'Aceptada con avisos',
                description: warnings.join(' · '),
                variant: 'destructive',
            });
        } else {
            const bits = [
                data.emailSent ? 'email enviado' : null,
                data.discordDmSent ? 'DM Discord' : null,
                data.roleAdded ? 'rol asignado' : (data.inviteSent ? 'invitación enviada' : null),
            ].filter(Boolean);
            toast({ title: '¡Solicitud aceptada!', description: `Cuenta creada${bits.length ? ' — ' + bits.join(', ') : ''}.` });
        }
        await refetch();
    } catch (error: any) {
        console.error("Error approving application:", error);
        toast({ title: 'Error', description: error?.message || 'No se pudo aprobar la solicitud.', variant: 'destructive' });
    } finally {
        setIsProcessing(null);
    }
  };

  const rejectAllPending = async () => {
    setIsBulkRejecting(true);
    try {
        const fn = httpsCallable(functions, 'rejectAllPendingApplications');
        const res: any = await fn({});
        toast({ title: 'Hecho', description: `${res.data?.rejected ?? 0} solicitud(es) marcadas como rechazadas.` });
        await refetch();
    } catch (error: any) {
        console.error("Error bulk-rejecting applications:", error);
        toast({ title: 'Error', description: error?.message || 'No se pudieron rechazar las solicitudes.', variant: 'destructive' });
    } finally {
        setIsBulkRejecting(false);
    }
  };

  const handleAction = async (actionType: 'reject', app: Application) => {
    if (!user) {
        toast({ title: 'Error', description: 'Debes estar autenticado como administrador.', variant: 'destructive'});
        return;
    }
    
    setIsProcessing(app.id);

    if (actionType === 'reject') {
      const result = await rejectApplication(app.id);
       if (result.success) {
        toast({ title: 'Éxito', description: result.message });
        await refetch();
      } else {
        toast({ title: 'Error', description: result.error, variant: 'destructive' });
      }
    }
    
    setIsProcessing(null);
  };
  
  const filteredApplications = useMemo(() => {
    if (!applications) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return applications;
    return applications.filter(app =>
        app.name?.toLowerCase().includes(q) ||
        app.username?.toLowerCase().includes(q) ||
        app.email?.toLowerCase().includes(q) ||
        app.discordId?.toLowerCase().includes(q) ||
        app.minecraftId?.toLowerCase().includes(q) ||
        app.reason?.toLowerCase().includes(q) ||
        app.country?.toLowerCase().includes(q) ||
        app.province?.toLowerCase().includes(q)
    );
  }, [applications, searchQuery]);

  // Resumen de todas las solicitudes recibidas (no depende del buscador).
  const stats = useMemo(() => {
    const list = applications || [];
    const total = list.length;
    const approved = list.filter((a) => a.status === 'approved').length;
    const rejected = list.filter((a) => a.status === 'rejected').length;
    return {
      total,
      approved,
      rejected,
      pctApproved: total ? (approved / total) * 100 : 0,
      pctRejected: total ? (rejected / total) * 100 : 0,
    };
  }, [applications]);

  const isLoading = loading || isLoadingApplications;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-2">Cargando solicitudes...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-destructive text-center">Error al cargar las solicitudes: {(error as Error).message}</p>;
  }

  if (!applications || applications.length === 0) {
    return <p className="text-muted-foreground text-center">No hay solicitudes pendientes.</p>;
  }

  
  const formatDate = (dateValue: any) => {
    if (!dateValue) return 'Fecha desconocida';
    try {
      const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      return formatDistanceToNow(date, { addSuffix: true, locale: es });
    } catch(e) {
      console.error("Error formatting date:", dateValue, e);
      return 'Fecha inválida';
    }
  };

  const isActionDisabled = (app: Application) => {
      return app.status !== 'pending' || !!isProcessing;
  }

  const pendingCount = applications.filter(a => a.status === 'pending' || a.status === 'preparing_approval').length;

  return (
    <>
      {/* Resumen de solicitudes recibidas */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-muted-foreground">Formularios enviados</p>
          <p className="mt-1.5 text-3xl font-black tabular-nums text-primary">{stats.total}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">en total</p>
        </div>
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-muted-foreground">Aceptados</p>
          <p className="mt-1.5 text-3xl font-black tabular-nums text-emerald-400">{stats.pctApproved.toFixed(1)}%</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{stats.approved} solicitud{stats.approved === 1 ? '' : 'es'}</p>
        </div>
        <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-muted-foreground">Rechazados</p>
          <p className="mt-1.5 text-3xl font-black tabular-nums text-rose-400">{stats.pctRejected.toFixed(1)}%</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{stats.rejected} solicitud{stats.rejected === 1 ? '' : 'es'}</p>
        </div>
      </div>

      {/* Bulk actions */}
      {pendingCount > 0 && (
        <div className="flex items-center justify-between gap-3 mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-amber-400">{pendingCount}</span> solicitud{pendingCount !== 1 ? 'es' : ''} sin corregir.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isBulkRejecting} className="border-destructive/40 text-destructive hover:bg-destructive/10">
                {isBulkRejecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
                Rechazar todas las pendientes
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Rechazar {pendingCount} solicitud{pendingCount !== 1 ? 'es' : ''}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se marcarán como rechazadas todas las solicitudes en estado pendiente o procesando. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={rejectAllPending} className="bg-destructive hover:bg-destructive/80">
                  Confirmar rechazo masivo
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, email, Discord, Minecraft, país..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
        {searchQuery && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {filteredApplications.length} resultado{filteredApplications.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {filteredApplications.length === 0 && searchQuery ? (
        <p className="text-center text-muted-foreground py-8">No se encontraron solicitudes para "{searchQuery}".</p>
      ) : (
      <div className="space-y-4">
        {filteredApplications.map((app) => (
          <Card key={app.id} className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between bg-muted/30 p-4">
              <div className="flex items-center gap-4">
                  <User className="h-6 w-6 text-primary" />
                  <CardTitle className="text-xl">{app.name}, {app.age}</CardTitle>
              </div>
              <Badge variant={statusConfig[app.status]?.variant} className={statusConfig[app.status]?.className}>
                {app.status === 'preparing_approval' && <Hourglass className="mr-2 h-4 w-4 animate-spin" />}
                {statusConfig[app.status]?.label || app.status.toUpperCase()}
              </Badge>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{app.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>Enviado {formatDate(app.submissionDate)}</span>
                  </div>
              </div>

              <Accordion type="single" collapsible>
                  <AccordionItem value="details">
                      <AccordionTrigger>Ver todos los detalles</AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div><strong className="block text-muted-foreground">ID Discord:</strong> {app.discordId}</div>
                              <div><strong className="block text-muted-foreground">ID Minecraft:</strong> {app.minecraftId}</div>
                              <div><strong className="block text-muted-foreground">ID Usuario:</strong> {app.username}</div>
                              <div><strong className="block text-muted-foreground">País/Prov:</strong> {app.country}, {app.province}</div>
                              <div><strong className="block text-muted-foreground">Conoc. Minecraft:</strong> {knowledgeMapping[app.minecraftKnowledge] || app.minecraftKnowledge}</div>
                              <div><strong className="block text-muted-foreground">Conoc. Mods:</strong> {knowledgeMapping[app.modKnowledge] || app.modKnowledge}</div>
                              <div><strong className="block text-muted-foreground">Actividad:</strong> {knowledgeMapping[app.serverActivity] || app.serverActivity}</div>
                              <div><strong className="block text-muted-foreground">Fuente:</strong> {knowledgeMapping[app.source] || app.source}</div>
                          </div>
                          <div>
                              <strong className="block text-muted-foreground mb-1">Razón para unirse:</strong>
                              <p className="text-muted-foreground bg-muted/50 p-3 rounded-md border">{app.reason}</p>
                          </div>
                      </AccordionContent>
                  </AccordionItem>
              </Accordion>
              
            </CardContent>
            <CardFooter className="bg-muted/30 p-4 flex justify-end gap-2">
                  <AlertDialog>
                      <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" disabled={isActionDisabled(app)}>
                            {isProcessing === app.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <X className="mr-2 h-4 w-4" />}
                            Rechazar
                          </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                          <AlertDialogHeader>
                              <AlertDialogTitle>¿Seguro que quieres rechazar?</AlertDialogTitle>
                              <AlertDialogDescription>Esta acción es permanente y no se puede deshacer.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleAction('reject', app)} className="bg-destructive hover:bg-destructive/80">
                                Confirmar Rechazo
                              </AlertDialogAction>
                          </AlertDialogFooter>
                      </AlertDialogContent>
                  </AlertDialog>

                  <Button size="sm" disabled={isActionDisabled(app)} onClick={() => approveApplication(app.id)}>
                      {isProcessing === app.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Check className="mr-2 h-4 w-4" />}
                      Aceptar
                  </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
      )}
    </>
  );
}
    